import React from "react";
import { Box } from "@mui/material";
import UserInputWithChip from "./UserInputWithChip";
import InputWithTooltip from "./InputWithTooltip";
import { styles } from "./UserInputForm.styles";
import TldCheckboxDropdown from "./TldCheckboxDropdown";
import InputAccordion from "./InputAccordion";
import { formDataLimits, checkLimit } from "../../constants/formDataLimits";
import { FONT_SIZES } from "../../constants/fontSizes";

const DEFAULT_TLDS = [".com", ".net", ".io", ".ai", ".org"];

const fieldExplanations = {
  domain_name_candidates:
    "Optional: Comma separated list of potential domain names you're considering. These names will be checked for availability across different TLDs. You can also iterate by generating a few rounds of names and keep only what you like. (Max 20 names)",
  num_domain_names:
    "Specify how many alternative domain name suggestions you would like to receive. The system will generate this many additional domain options based on your input. (Max 20 names)",
  project_description:
    "Provide a detailed description of your project or business. This will influence the names that are generated.",
};

const DomainInputForm = ({
  formData,
  setFormData,
  disabled,
  onClearAllNames,
  trademarkCandidates,
}) => {
  // Ensure tlds has a default value
  const selectedTlds = formData?.tlds || DEFAULT_TLDS;

  // Add effect to update domain candidates when trademark candidates change
  React.useEffect(() => {
    if (trademarkCandidates && trademarkCandidates.length > 0) {
      // Combine existing domain candidates with trademark candidates, avoiding duplicates
      const existingCandidates = formData.domain_name_candidates || [];
      const newCandidates = trademarkCandidates.filter(
        (name) => !existingCandidates.includes(name)
      );

      if (newCandidates.length > 0) {
        const combinedCandidates = [...existingCandidates, ...newCandidates];
        const { value } = checkLimit(
          "domain_name_candidates",
          combinedCandidates
        );
        setFormData((prev) => ({
          ...prev,
          domain_name_candidates: value,
        }));
      }
    }
  }, [trademarkCandidates]);

  const handleProjectDescriptionChange = (e) => {
    const { value } = checkLimit("project_description", e.target.value);
    setFormData((prev) => ({
      ...prev,
      project_description: value,
    }));
  };

  const handleDomainNamesChange = (newNames) => {
    const { value } = checkLimit("domain_name_candidates", newNames);
    setFormData((prev) => ({
      ...prev,
      domain_name_candidates: value,
    }));
  };

  const handleTldsChange = (newTlds) => {
    const { value } = checkLimit("tlds", newTlds);
    setFormData((prev) => ({
      ...prev,
      tlds: value,
    }));
  };

  const handleNumDomainNamesChange = (e) => {
    // Only update if the value is a valid number or empty
    const value = e.target.value;
    if (value === "" || !isNaN(value)) {
      setFormData((prev) => ({
        ...prev,
        num_domain_names: value,
      }));
    }
  };

  const handleNumDomainNamesBlur = (e) => {
    const { value } = checkLimit(
      "num_domain_names",
      e.target.value || 1,
      formData.domain_name_candidates?.length || 0
    );
    setFormData((prev) => ({
      ...prev,
      num_domain_names: value,
    }));
  };

  return (
    <InputAccordion
      title="Domain Name Settings"
      expanded={formData?.job_type === "domain"}
    >
      <Box>
        {formData.job_type === "domain" && (
          <InputWithTooltip
            fullWidth
            multiline
            minRows={1}
            maxRows={5}
            label="Project Description"
            name="project_description"
            value={formData.project_description}
            onChange={handleProjectDescriptionChange}
            required
            disabled={disabled}
            size="small"
            sx={styles.projectDescription}
            tooltipText={fieldExplanations.project_description}
          />
        )}
        {formData.job_type === "domain" && (
          <InputWithTooltip
            fullWidth
            type="number"
            label="Nr. of Domain Names to Generate"
            name="num_domain_names"
            value={formData.num_domain_names}
            onChange={handleNumDomainNamesChange}
            onBlur={handleNumDomainNamesBlur}
            required
            disabled={disabled}
            inputProps={{
              min: formDataLimits.num_domain_names.min,
              max: formDataLimits.num_domain_names.max,
            }}
            size="small"
            tooltipText={fieldExplanations.num_domain_names}
          />
        )}
        <TldCheckboxDropdown
          selectedTlds={selectedTlds}
          onChange={handleTldsChange}
          disabled={disabled}
        />
        <UserInputWithChip
          label="Domain Name Candidates"
          value={
            formData.domain_name_candidates &&
            formData.domain_name_candidates.length > 0
              ? formData.domain_name_candidates
              : formData.candidate_names && formData.candidate_names.length > 0
              ? formData.candidate_names
              : []
          }
          inputBoxValue={formData.domain_name_candidates_input_box || ""}
          handleInputBoxChange={(inputBoxValue) =>
            setFormData((prev) => ({
              ...prev,
              domain_name_candidates_input_box: inputBoxValue,
            }))
          }
          handleChipChange={handleDomainNamesChange}
          disabled={disabled}
          tooltipText={fieldExplanations.domain_name_candidates}
          name="domain_name_candidates"
          helperText="Type a name (or comma separated list) and press Enter to add."
          helperTextProps={{ sx: { fontSize: FONT_SIZES.TEXT_HINT } }}
          onClearAll={onClearAllNames}
        />
      </Box>
    </InputAccordion>
  );
};

export default DomainInputForm;
